import { memo, useMemo } from 'react'
import { Clock3, Pause, Play, Square } from 'lucide-react'
import PressurePanel from './PressurePanel'

type LayerKey = 'flights' | 'satellites' | 'maritime' | 'jamming' | 'events' | 'notams' | 'earthquakes' | 'volcanoes' | 'tsunami' | 'news' | 'cameras'

type Props = {
  cls: (...parts: Array<string | false | null | undefined>) => string
  apiFetch: typeof fetch
  viewBounds: any
  pressureSeries: any
  pressureLoading: boolean
  pressureError: string
  pressureDetail: any
  activePressureCategory: string
  visiblePressureCategories: string[]
  pressureRegionMode: 'viewport' | 'radius'
  pressureRadiusKm: number
  pressurePresets: Array<{ id: string; label: string; center: { lat: number; lon: number }; radiusKm: number }>
  activePressurePresetId: string
  setActivePressureCategory: (value: string) => void
  setVisiblePressureCategories: (value: string[]) => void
  setPressureRegionMode: (value: 'viewport' | 'radius') => void
  setPressureRadiusKm: (value: number) => void
  setPressureDetail: (value: any) => void
  historyMode: 'live' | 'history'
  setHistoryMode: (value: 'live' | 'history') => void
  historyPlaying: boolean
  setHistoryPlaying: (value: boolean) => void
  historyEntries: Array<{ filename: string; label: string }>
  selectedHistoryIndex: number
  setSelectedHistoryIndex: (value: number | ((current: number) => number)) => void
  setTimelineMinute: (value: number) => void
  selectedHistoryEntry: { label: string } | null
  historyLoading: boolean
  visibleFeed: Array<unknown>
  displayFeed: Array<unknown>
  layerLabels: Record<LayerKey, string>
  layers: Record<LayerKey, boolean>
  setLayers: React.Dispatch<React.SetStateAction<Record<LayerKey, boolean>>>
  stockTicker: Array<{ symbol: string; price: number; change: number }>
  setActiveStock: (value: any) => void
}

function WorldviewTimelineStrip(props: Props) {
  const {
    cls,
    apiFetch,
    viewBounds,
    pressureSeries,
    pressureLoading,
    pressureError,
    pressureDetail,
    activePressureCategory,
    visiblePressureCategories,
    pressureRegionMode,
    pressureRadiusKm,
    pressurePresets,
    activePressurePresetId,
    setActivePressureCategory,
    setVisiblePressureCategories,
    setPressureRegionMode,
    setPressureRadiusKm,
    setPressureDetail,
    historyMode,
    setHistoryMode,
    historyPlaying,
    setHistoryPlaying,
    historyEntries,
    selectedHistoryIndex,
    setSelectedHistoryIndex,
    setTimelineMinute,
    selectedHistoryEntry,
    historyLoading,
    visibleFeed,
    displayFeed,
    layerLabels,
    layers,
    setLayers,
    stockTicker,
    setActiveStock,
  } = props

  const timelineMarkerEntries = useMemo(() => historyEntries.slice(0, 24), [historyEntries])
  const marketTickerItems = useMemo(() => [...stockTicker, ...stockTicker], [stockTicker])

  return (
    <>
      <PressurePanel
        viewBounds={viewBounds}
        pressureSeries={pressureSeries}
        pressureLoading={pressureLoading}
        pressureError={pressureError}
        pressureDetail={pressureDetail}
        activePressureCategory={activePressureCategory}
        visiblePressureCategories={visiblePressureCategories}
        pressureRegionMode={pressureRegionMode}
        pressureRadiusKm={pressureRadiusKm}
        pressurePresets={pressurePresets}
        activePressurePresetId={activePressurePresetId}
        setActivePressureCategory={setActivePressureCategory}
        setVisiblePressureCategories={setVisiblePressureCategories}
        setPressureRegionMode={setPressureRegionMode}
        setPressureRadiusKm={setPressureRadiusKm}
        onSelectPressurePreset={(presetId: string) => {
          const preset = pressurePresets.find(item => item.id === presetId)
          setActivePressureCategory(activePressureCategory)
          setPressureRegionMode(pressureRegionMode)
          if (preset) setPressureRadiusKm(preset.radiusKm)
        }}
        onSelectSpike={async (timestamp: string) => {
          if (!viewBounds) return
          const activePreset = pressurePresets.find(preset => preset.id === activePressurePresetId) || pressurePresets[0]
          const region = pressureRegionMode === 'radius'
            ? { mode: 'radius', center: activePreset?.center || { lat: (viewBounds.north + viewBounds.south) / 2, lon: (viewBounds.east + viewBounds.west) / 2 }, radiusKm: pressureRadiusKm }
            : { mode: 'viewport', bounds: viewBounds }
          const res = await apiFetch('/api/pressure/spike-detail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              region,
              bucketSize: 'hour',
              timestamp,
              category: activePressureCategory,
            }),
          })
          if ((res as any).ok) setPressureDetail(await (res as any).json())
        }}
      />

      <div className="timeline-shell tactical-timeline-shell worldview-bottom-command-strip">
        <div className="timeline-header tactical-timeline-header">
          <div className="eyebrow"><Clock3 size={14} /> Mission Timeline</div>
          <div className="tactical-playback-controls">
            <button className={cls('pill timeline-mode-pill', historyMode === 'live' && 'active')} onClick={() => { setHistoryMode('live'); setHistoryPlaying(false); setSelectedHistoryIndex(-1); setTimelineMinute(0) }}>LIVE</button>
            <button className={cls('pill timeline-mode-pill', historyMode === 'history' && 'active')} onClick={() => { setHistoryMode('history'); if (historyEntries.length > 0 && selectedHistoryIndex < 0) setSelectedHistoryIndex(historyEntries.length - 1) }}>HISTORY</button>
            <button className="pill timeline-icon-pill" onClick={() => {
              if (historyMode !== 'history' || historyEntries.length === 0) return
              setHistoryPlaying(true)
            }} title="Play history" disabled={historyMode !== 'history' || historyEntries.length === 0}><Play size={11} /></button>
            <button className="pill timeline-icon-pill" onClick={() => setHistoryPlaying(false)} title="Pause history" disabled={historyMode !== 'history'}><Pause size={11} /></button>
            <button className="pill timeline-icon-pill" onClick={() => { setHistoryPlaying(false); if (historyMode === 'history' && historyEntries.length > 0) setSelectedHistoryIndex(historyEntries.length - 1) }} title="Stop and reset" disabled={historyMode !== 'history'}><Square size={11} /></button>
            <span className="timeline-live-readout">{historyMode === 'history' && selectedHistoryEntry ? `${selectedHistoryEntry.label}${historyPlaying ? ' · PLAY' : ' · HOLD'}` : 'LIVE EDGE'}</span>
          </div>
        </div>

        <div className="worldview-bottom-meta-row">
          <div className="worldview-layer-chips">
            {(Object.keys(layerLabels) as LayerKey[]).filter(key => ['flights', 'maritime', 'notams', 'news', 'events', 'satellites'].includes(key)).map(key => (
              <button
                key={`bottom-${key}`}
                className={cls('worldview-layer-chip', layers[key] && 'active')}
                onClick={() => setLayers(c => ({ ...c, [key]: !c[key] }))}
              >
                {layerLabels[key]}
              </button>
            ))}
          </div>
          <div className="worldview-bottom-ticker market-ticker-shell">
            <span className="stat-kicker">MKT</span>
            <div className="market-ticker-track">
              {marketTickerItems.map((stock, index) => (
                <button
                  key={`bottom-${stock.symbol}-${index}`}
                  onClick={() => setActiveStock(stock)}
                  className="market-ticker-chip"
                >
                  <strong style={{ letterSpacing: '0.08em' }}>{stock.symbol}</strong>
                  <span style={{ fontFamily: 'monospace', color: '#9fb3c6' }}>{typeof stock.price === 'number' && Number.isFinite(stock.price) && stock.price > 0 ? `$${stock.price.toFixed(2)}` : 'PRIVATE'}</span>
                  <span style={{ color: typeof stock.change === 'number' && stock.change >= 0 ? '#6cf0a8' : '#ff8e8e', fontFamily: 'monospace' }}>{typeof stock.change === 'number' && stock.change >= 0 ? '+' : ''}{typeof stock.change === 'number' && Number.isFinite(stock.change) ? stock.change.toFixed(2) : '?.??'}%</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="timeline-track-shell">
          <input
            className="timeline-slider tactical-timeline-slider"
            type="range"
            min={0}
            max={Math.max(0, historyEntries.length - 1)}
            value={Math.max(0, selectedHistoryIndex < 0 ? historyEntries.length - 1 : selectedHistoryIndex)}
            onChange={e => {
              const next = Number(e.target.value)
              setHistoryMode('history')
              setHistoryPlaying(false)
              setSelectedHistoryIndex(next)
            }}
          />
          <div className="timeline-markers tactical-markers">
            {timelineMarkerEntries.map((entry, index) => (
              <button
                key={entry.filename}
                className="timeline-marker tactical-marker"
                style={{ left: `${(index / Math.max(1, timelineMarkerEntries.length - 1)) * 100}%`, borderColor: selectedHistoryIndex === index ? '#54d5ff' : '#3a6f88' }}
                onClick={() => {
                  setHistoryMode('history')
                  setHistoryPlaying(false)
                  setSelectedHistoryIndex(index)
                }}
              ><span>{`ARCH ${index + 1}`}</span></button>
            ))}
          </div>
        </div>

        <div className="timeline-caption tactical-caption">
          {historyMode === 'history'
            ? selectedHistoryEntry
              ? `Reviewing archived worldview snapshot from ${selectedHistoryEntry.label}. Use play, pause, stop, or scrub the slider to move through retained history events.`
              : historyLoading
                ? 'Loading archived worldview history...'
                : 'No archived worldview snapshots available yet.'
            : viewBounds
              ? `Showing ${visibleFeed.length} events in current view. Switch to history to replay archived event states, or return to live edge anytime.`
              : `${displayFeed.length} events globally in last 24 h. Switch to history mode to replay archived event states.`}
        </div>
      </div>
    </>
  )
}

export default memo(WorldviewTimelineStrip)
